import { Card, Grid } from "@mui/material";
import { useEffect, useState } from "react";

import Box from "../../components/Box";
import Helpers from "../../commons/helpers";
import Button from "../../components/Button";
import Switch from "../../components/Switch";
import useStrings from "../../hooks/useStrings";
import FormField from "../../components/FormField";
import DataTable from "../../components/DataTable";
import Typography from "../../components/Typography";
import ModalCreateEditCodeName from "./modalCreateEdit";
import CustomStatusCell from "../../components/CustomStatusCell";
import CodeNameService, { IDataCreateUpdateCodename } from "../../services/common/codeName.service";

import { MainComponent } from "../main";
import { Mode, Status } from "../../constants/enums";
import { Constants } from "../../constants/constants";
import { IRecordCodename, IRequestGetPagedCodename } from "./list";
import { IInput, IPropDetailContainer } from "../../commons/interfaces";
import { useCommonComponentContext } from "../../providers/CommonComponentProvider";



export interface IDataCodename {
    id?: string;
    code?: IInput;
    name?: IInput;
    shortName?: IInput;
    group?: string;
    isPublic?: boolean;
    serviceCode?: string;
    organizationId?: string;
    updateTime?: string;
    status?: number;
}

const CodenameEditContainer = (props: IPropDetailContainer & {
    hidenActionCreate?: boolean;
    hidenActionUpdate?: boolean;
    hidenActionDelete?: boolean;
}) => {
    const Strings = useStrings();
    const {
        userInfo,
        serviceCode,
        onError,
        onSuccess,
        onShowLoading,
        onHideLoading,
    } = useCommonComponentContext();

    const [isOnChange, setIsOnChange] = useState<boolean>(false);
    const [requestData, setRequestData] = useState<IRequestGetPagedCodename>();
    const [dataRowsChild, setDataRowsChild] = useState<IRecordCodename[]>([]);

    const [openModal, setOpenModal] = useState<boolean>(false);
    const [dataCodeName, setDataCodeName] = useState<IDataCodename>({});
    const [dataModalCodeName, setDataModalCodeName] = useState<IDataCodename>({});

    useEffect(() => {
        if (props.hidenActionUpdate) {
            props.onChangeMode(Mode.View);
        }
    }, [props.hidenActionUpdate]);

    useEffect(() => {
        (async () => {
            try {
                onShowLoading();

                if (Helpers.isNullOrEmpty(props.idDetail)) {
                    const dataDetail: IDataCodename = {
                        code: { value: "" },
                        name: { value: "" },
                        shortName: { value: "" },
                        serviceCode: serviceCode,
                        organizationId: userInfo?.userProfile?.organizationId || "0",
                        isPublic: false,
                    };

                    setDataCodeName(dataDetail);
                } else {
                    const detailItem: any = await CodeNameService.getDetail(props.idDetail);

                    const dataDetail: IDataCodename = {
                        id: detailItem?.id,
                        group: detailItem?.group,
                        code: { value: detailItem?.code },
                        name: { value: detailItem?.name },
                        shortName: { value: detailItem?.shortName },
                        organizationId: detailItem?.organizationId,
                        serviceCode: detailItem?.serviceCode,
                        isPublic: detailItem?.organizationId === "0" ? true : false,
                        updateTime: detailItem?.updateTime,
                        status: detailItem?.status,
                    };

                    setDataCodeName(dataDetail);

                    await getPagedChild({
                        group: detailItem?.code,
                        organizationId: detailItem?.organizationId,
                        pageNumber: 1,
                        pageSize: Constants.ROW_PER_PAGE,
                    });

                    if (detailItem?.status === Status.Inactive) {
                        props.onChangeMode(Mode.View);
                    }
                }

                onHideLoading();
            } catch (error) {
                onHideLoading();
                const e = Helpers.renderExceptionError(error);
                onError(e);
            }
        })()
    }, [props.idDetail, userInfo?.userProfile?.organizationId]);

    const handleOnchangeValue = (key: string, val: any) => {
        setIsOnChange(true);
        setDataCodeName(prev => {
            let dataTemp: any = { ...prev };
            dataTemp[key] = { value: val, error: "" };

            if (key === "isPublic") {
                dataTemp["isPublic"] = val;
                dataTemp["organizationId"] = undefined;
            }

            return dataTemp;
        });
    };

    const handleCreateUpdateCodename = async () => {
        let isCheck: boolean = true;
        let dataTemp: any = { ...dataCodeName };
        if (Helpers.isNullOrEmpty(dataTemp["code"]?.value)) {
            isCheck = false;
            dataTemp["code"] = { error: Strings.Common.REQUIRED };
        }
        if (Helpers.isNullOrEmpty(dataTemp["name"]?.value)) {
            isCheck = false;
            dataTemp["name"] = { error: Strings.Common.REQUIRED };
        }

        if (!isCheck) {
            setDataCodeName(dataTemp);
        } else {
            try {
                onShowLoading();

                const orgIDTemp = (dataCodeName?.organizationId || userInfo?.userProfile?.organizationId || "0");

                let dataSubmit: IDataCreateUpdateCodename = {
                    group: dataTemp?.group,
                    code: dataTemp?.code?.value,
                    name: dataTemp?.name?.value,
                    shortName: dataTemp?.shortName?.value,
                    serviceCode: dataTemp?.serviceCode || serviceCode,
                    organizationId: (dataCodeName?.isPublic === true) ? "0" : orgIDTemp,
                };

                if (Helpers.isNullOrEmpty(dataCodeName?.id)) {
                    await CodeNameService.create(dataSubmit);

                    onSuccess(Strings.CODE_NAME.CREATE_SUCCESS);

                    props.onGoBack();
                } else {
                    await CodeNameService.update({
                        ...dataSubmit,
                        id: dataCodeName?.id,
                        updateTime: dataCodeName?.updateTime,
                    });

                    const detailItem: any = await CodeNameService.getDetail(props.idDetail);

                    const dataDetail: IDataCodename = {
                        id: detailItem?.id,
                        code: { value: detailItem?.code },
                        name: { value: detailItem?.name },
                        shortName: { value: detailItem?.shortName },
                        organizationId: detailItem?.organizationId,
                        serviceCode: detailItem?.serviceCode,
                        isPublic: detailItem?.organizationId === "0" ? true : false,
                        updateTime: detailItem?.updateTime,
                        status: detailItem?.status,
                    };

                    setDataCodeName(dataDetail);

                    await getPagedChild({
                        group: detailItem?.code,
                        organizationId: detailItem?.organizationId,
                        pageNumber: 1,
                        pageSize: Constants.ROW_PER_PAGE,
                    });

                    onSuccess(Strings.CODE_NAME.UPDATE_SUCCESS);

                    props.onChangeMode(Mode.View);
                }

                onHideLoading();
            } catch (error) {
                onHideLoading();
                const e = Helpers.renderExceptionError(error);
                onError(e);
            }
        }
    };

    const renderCodeNameInfo = () => {
        return (
            <Card>
                <Box p={3}>
                    <Box component="form">
                        <Grid container spacing={3}>
                            <Grid item xs={12} alignItems="center" sx={{ display: "flex", flexWrap: "wrap" }}>
                                <Typography variant="h5">{Strings.CODE_NAME.BASIC_INFO}</Typography>
                                {dataCodeName?.status &&
                                    <Box ml={2}>
                                        <CustomStatusCell status={dataCodeName?.status} isDetail />
                                    </Box>
                                }
                            </Grid>
                            <Grid item xs={12} md={6} display="flex" alignItems="end">
                                <FormField
                                    required
                                    maxLength={255}
                                    mode={props.mode}
                                    label={Strings.CODE_NAME.CODE_GROUP}
                                    errorMessage={dataCodeName?.code?.error}
                                    defaultValue={dataCodeName?.code?.value || ""}
                                    placeholder={Strings.CODE_NAME.ENTER_CODE_GROUP}
                                    disabled={!Helpers.isNullOrEmpty(dataCodeName?.id)}
                                    onBlur={(value) => {
                                        handleOnchangeValue("code", value);
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormField
                                    required
                                    maxLength={255}
                                    mode={props.mode}
                                    label={Strings.CODE_NAME.NAME_GROUP}
                                    errorMessage={dataCodeName?.name?.error}
                                    defaultValue={dataCodeName?.name?.value || ""}
                                    placeholder={Strings.CODE_NAME.ENTER_NAME_GROUP}
                                    onBlur={(value) => {
                                        handleOnchangeValue("name", value);
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormField
                                    maxLength={255}
                                    mode={props.mode}
                                    label={Strings.CODE_NAME.SHORT_NAME}
                                    errorMessage={dataCodeName?.shortName?.error}
                                    placeholder={Strings.CODE_NAME.ENTER_SHORT_NAME}
                                    defaultValue={dataCodeName?.shortName?.value || ""}
                                    onBlur={(value) => {
                                        handleOnchangeValue("shortName", value);
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={12}>
                                <Box sx={{
                                    gap: 1,
                                    display: "flex",
                                    flexWrap: "wrap",
                                    alignItems: "center",
                                }}>
                                    <Switch
                                        key={dataCodeName?.organizationId}
                                        defaultChecked={dataCodeName?.isPublic || false}
                                        disabled={!Helpers.isNullOrEmpty(dataCodeName?.id)}
                                        onChange={(event, checked) => {
                                            handleOnchangeValue("isPublic", checked)
                                        }}
                                    />
                                    <Typography variant="button" fontWeight="bold">
                                        {Strings.CODE_NAME.APPLY_SYSTEM}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </Box>
            </Card>
        );
    };

    // Codename Child
    const getPagedChild = async (dataReq?: IRequestGetPagedCodename) => {
        try {
            onShowLoading();

            const pageSize = dataReq?.pageSize || Constants.ROW_PER_PAGE;
            const pageNumber = Helpers.getPageNumber(dataReq?.pageNumber || 1, pageSize, dataReq?.totalCount || 0);

            const newRequest: any = {
                pageSize,
                pageNumber,
                group: dataReq?.group,
                name: dataReq?.searchText,
                organizationId: dataReq?.organizationId,
                serviceCode: serviceCode,
            };

            const result = await CodeNameService.getPaged(newRequest);

            setDataRowsChild([...result.items || []]);
            setRequestData({
                ...newRequest,
                searchText: newRequest?.name,
                totalCount: result.totalCount,
            });

            onHideLoading();
        } catch (error) {
            onHideLoading();
            const e = Helpers.renderExceptionError(error);
            onError(e);
        }
    };

    const handleDeleteRowChild = async (id: string) => {
        Helpers.showConfirmAlert(Strings.CODE_NAME.CONFIRM_DELETE, async () => {
            try {
                onShowLoading();

                const result = await CodeNameService.delete(id);
                if (result.statusCode === 200) {
                    await getPagedChild(requestData);
                    onSuccess(Strings.CODE_NAME.DELETE_SUCCESS);
                }

                onHideLoading();
            } catch (error) {
                onHideLoading();
                const e = Helpers.renderExceptionError(error);
                onError(e);
            }
        });
    };

    const renderListCodeNameChild = () => {
        return (
            <Card>
                <Box p={3}>
                    <Box display="flex" justifyContent="space-between">
                        <Typography variant="h5">{Strings.CODE_NAME.CODE_NAME_DETAIL}</Typography>
                        {
                            (!props.hidenActionCreate || !props.hidenActionUpdate) &&
                            (dataCodeName?.id && props.mode !== Mode.View) &&
                            <Button onClick={() => { setOpenModal(true); }}>
                                {Strings.Common.ADD_NEW}
                            </Button >

                        }
                    </Box>
                    <Box>
                        <DataTable
                            mode={props.mode}
                            rowPerPage={requestData?.pageSize}
                            pageNumber={requestData?.pageNumber}
                            totalCount={requestData?.totalCount}
                            onChangePageSize={(pageSize) => {
                                getPagedChild({ ...requestData, group: dataCodeName?.code?.value, pageSize });
                            }}
                            onChangePageNumber={(pageNumber) => {
                                getPagedChild({ ...requestData, group: dataCodeName?.code?.value, pageNumber });
                            }}

                            searchText={requestData?.searchText}
                            placeholderSearch={Strings.CODE_NAME.SEARCH_TEXT}
                            onSearchChange={(val: string) => {
                                setRequestData({ ...requestData, group: dataCodeName?.code?.value, searchText: val })
                            }}
                            onSearch={(val) => {
                                getPagedChild({ ...requestData, pageNumber: 1, group: dataCodeName?.code?.value, searchText: val })
                            }}

                            table={{
                                rows: dataRowsChild || [],
                                columns: [
                                    { Header: Strings.CODE_NAME.CODE, accessor: "code", width: "120px", },
                                    { Header: Strings.CODE_NAME.NAME, accessor: "name", width: "140px" },
                                    { Header: Strings.CODE_NAME.SHORT_NAME, accessor: "shortName", width: "150px", },
                                    {
                                        Header: Strings.Common.STATUS, accessor: "status", width: "150px",
                                        Cell: ({ value }: any) => <CustomStatusCell status={value} />
                                    },
                                ],
                            }}

                            actionList={row => [
                                (row?.status !== Status.Inactive) && !props.hidenActionUpdate && {
                                    key: "edit",
                                    iconName: "edit",
                                    title: Strings.Common.EDIT,
                                    callback: (row) => {
                                        setOpenModal(true);
                                        setDataModalCodeName({
                                            id: row?.id,
                                            group: row?.group,
                                            code: { value: row?.code },
                                            name: { value: row?.name },
                                            shortName: { value: row?.shortName },
                                            organizationId: row?.organizationId,
                                            isPublic: (row?.organizationId === "0") ? true : false,
                                            serviceCode: row?.serviceCode,
                                            updateTime: row?.updateTime,
                                            status: row?.status,
                                        });
                                    },
                                },
                                (row?.status !== Status.Inactive) && !props.hidenActionDelete && {
                                    key: "delete",
                                    iconName: "delete",
                                    title: Strings.Common.DELETE,
                                    callback: (row) => { handleDeleteRowChild(row.id) },
                                },
                            ]}
                        />
                    </Box>
                </Box>
                <ModalCreateEditCodeName
                    mode={props.mode}
                    openModal={openModal}
                    group={dataCodeName?.code?.value}
                    dataDetailCodename={dataModalCodeName}
                    onClose={() => {
                        setOpenModal(false);
                        setDataModalCodeName({} as IDataCodename);
                    }}
                    onCallBack={(data: IRecordCodename, isCreate: boolean) => {
                        let rows = [...dataRowsChild || []];

                        if (isCreate) {
                            rows.push({ ...data });

                            setOpenModal(false);
                            setDataRowsChild(rows);
                        } else {
                            const rows = [...dataRowsChild || []].map(el => {
                                return (el.id === data?.id) ? { ...data } : { ...el }
                            });

                            setOpenModal(false);
                            setDataRowsChild(rows);
                            setDataModalCodeName({} as IDataCodename);
                        }

                    }}
                />
            </Card >
        )
    };


    return (
        <MainComponent>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "end" }}>
                        <Button color={"secondary"} onClick={() => {
                            if (isOnChange) {
                                Helpers.showConfirmAlert(Strings.Common.CANCEL_ACTION_MESSAGE, props.onGoBack);
                            } else {
                                props.onGoBack();
                            }
                        }}>
                            {Strings.Common.GO_BACK}
                        </Button>
                        {(props.mode === Mode.View) && !props.hidenActionUpdate &&
                            <Button onClick={() => { props.onChangeMode(Mode.Update); }}>
                                {Strings.Common.EDIT}
                            </Button>
                        }

                        {(props.mode === Mode.Update) && !props.hidenActionUpdate && (
                            <Button onClick={() => { handleCreateUpdateCodename(); }}>
                                {Strings.Common.SAVE}
                            </Button>
                        )}

                        {(props.mode === Mode.Create) && !props.hidenActionCreate && (
                            <Button onClick={() => { handleCreateUpdateCodename(); }}>
                                {Strings.Common.SAVE}
                            </Button>
                        )}
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    {renderCodeNameInfo()}
                </Grid>
                {!Helpers.isNullOrEmpty(dataCodeName?.id) &&
                    <Grid item xs={12}>
                        {renderListCodeNameChild()}
                    </Grid>
                }
            </Grid>
        </MainComponent>
    );
};

export default CodenameEditContainer;
